import { useCallback, useEffect } from 'react';
import { DEFAULT_LANGUAGE } from '@/constants/config';
import {
  getLocaleData,
  isLocaleLoaded,
  loadLocale,
  useLocaleStore,
} from '@/constants/translations';
import { useUIStore } from '@/store/ui';

function resolve(key, language) {
  if (typeof key !== 'string' || !key) return '';
  const keys = key.split('.');

  const lang = language || DEFAULT_LANGUAGE;
  let value = getLocaleData(lang);
  for (const k of keys) {
    value = value?.[k];
    if (value === undefined) break;
  }

  if (value === undefined && lang !== DEFAULT_LANGUAGE) {
    value = getLocaleData(DEFAULT_LANGUAGE);
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) break;
    }
  }

  return value === undefined ? key : value;
}

/** Replace `%NAME%`-style tokens (uppercase param keys) in translated strings. */
function applyTranslationParams(str, params) {
  if (typeof str !== 'string' || !params || typeof params !== 'object') return str;
  let out = str;
  for (const [k, v] of Object.entries(params)) {
    const token = `%${String(k).toUpperCase()}%`;
    if (out.includes(token)) out = out.split(token).join(String(v ?? ''));
  }
  return out;
}

/**
 * Load locale data, then persist the user's language preference.
 * Call from language pickers so UI never switches before strings are ready.
 */
export async function selectLanguage(language) {
  const lang = (language || DEFAULT_LANGUAGE).toLowerCase();
  await loadLocale(lang);
  useUIStore.getState().setLanguage(lang);
}

/** Eagerly load the persisted language on app boot (root layout). */
export function useLocaleBootstrap() {
  const language = useUIStore((state) => state.language) || DEFAULT_LANGUAGE;

  useEffect(() => {
    let cancelled = false;

    loadLocale(language).catch(() => {}).finally(() => {
      if (cancelled) return;
    });

    return () => {
      cancelled = true;
    };
  }, [language]);
}

export function useTranslation() {
  const language = useUIStore((state) => state.language) || DEFAULT_LANGUAGE;
  const localeReady = useLocaleStore((state) => {
    if (language === DEFAULT_LANGUAGE) return true;
    return Boolean(state.loaded[language]) || isLocaleLoaded(language);
  });

  useEffect(() => {
    if (language === DEFAULT_LANGUAGE || localeReady) return;
    loadLocale(language).catch(() => {});
  }, [language, localeReady]);

  const t = useCallback((key, params) => {
    const raw = resolve(key, language);
    return applyTranslationParams(raw, params);
  }, [language, localeReady]);

  return { t, currentLanguage: language, localeReady };
}

/**
 * Non-hook translation for use outside React components (store, socket, etc.).
 * Reads the current language from the Zustand store snapshot.
 */
export function getTranslation(key, params) {
  const language = useUIStore.getState().language || DEFAULT_LANGUAGE;
  const raw = resolve(key, language);
  return applyTranslationParams(raw, params);
}

export default useTranslation;
